import { ErrorOutlineRounded, Replay } from "@mui/icons-material";
import { Box, CircularProgress, Container, IconButton, Link, List, ListItem, Tooltip } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ConnectedHubIcon, DisconnectedHubIcon } from "assets/icons";
import { HubActions } from "components/hubs/HubActions";
import { HubsRestartTimeProps } from "components/hubs/HubRestartDialog";
import { HubSubNavBar } from "components/hubs/HubSubNavBar";
import { GROUNDLIGHT_SUPPORT_EMAIL } from "config/constants";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { HubWithStatus } from "models/hub";
import { Typography } from "new-components/typography/Typography";
import { getHub, getHubs } from "store/services/Hubs/hubsThunks";
import { handleResetHubs } from "store/slices/Hubs/hubsSlice";

export function HubsManagementPage() {
  const navigate = useNavigate();

  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(true);

  const { hubs, hubsLoading } = useAppSelector((state) => state.hubs);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getHubs());
  }, [dispatch]);

  useEffect(() => {
    if (hubs && hubs.length === 1) {
      navigate(`/reef/hubs/${hubs[0].id}`, {
        state: { hubName: hubs[0].name },
        replace: true,
      });
    }
  }, [hubs, navigate]);

  const getHubsDetails = useCallback(() => {
    try {
      if (hubs && isLoadingDetails) {
        setIsLoadingDetails(false);
        const promises = hubs.map((hub) => dispatch(getHub({ hubId: hub.id })));
        Promise.all(promises);
      }
    } catch (e) {
      console.error(e);
    }
  }, [hubs, isLoadingDetails, dispatch]);

  useEffect(() => {
    getHubsDetails();
  }, [getHubsDetails]);

  const refreshRestartingHubs = useCallback(() => {
    try {
      const restartTimesStored = localStorage.getItem("hubsRestartTime");
      if (restartTimesStored !== null) {
        const parsedRestartTimesStored = JSON.parse(restartTimesStored);
        const promises = parsedRestartTimesStored.map((elem: HubsRestartTimeProps) =>
          dispatch(getHub({ hubId: elem.id }))
        );
        Promise.all(promises);
      }
    } catch (e) {
      console.error(e);
    }
  }, [dispatch]);

  useEffect(() => {
    const refetchInterval = setInterval(() => {
      refreshRestartingHubs();
    }, 15000);

    return () => {
      clearTimeout(refetchInterval);
    };
  }, [refreshRestartingHubs]);

  useEffect(
    () => () => {
      dispatch(handleResetHubs());
    },
    [dispatch]
  );

  const getHubStylesByStatus = (hub: HubWithStatus) => {
    if (hub.isRestarting) {
      return {
        styles: { backgroundColor: "#E5E9ED", color: "#919599" },
        description: "Vision Core restarting. May take >5 minutes depending on configuration.",
        Icon: <CircularProgress size={19} color="inherit" />,
      };
    }

    if (hub.isLoading) {
      return {
        styles: { backgroundColor: "#E5E9ED", color: "#919599" },
        description: "Loading...",
        Icon: <CircularProgress size={19} color="inherit" />,
      };
    }

    if (hub.error) {
      return {
        styles: { backgroundColor: "#FFFFFF", color: "#49494D" },
        description: "replay",
        Icon: (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              dispatch(getHub({ hubId: hub.id }));
            }}
            style={{ marginLeft: "10px" }}
            color="primary"
            size="small"
          >
            <Replay />
          </IconButton>
        ),
      };
    }

    const { all_online: allOnline, is_online: isOnline } = hub.status_detail || {};

    if (!isOnline) {
      return {
        styles: { backgroundColor: "#E5E9ED", color: "#49494D" },
        description: "Cannot connect to Vision Core. Check the Vision Core’s internet connection.",
        Icon: (
          <Tooltip title="Not Operational" arrow>
            <DisconnectedHubIcon />
          </Tooltip>
        ),
      };
    }

    if (!allOnline) {
      return {
        styles: { backgroundColor: "#FFFFFF", color: "#49494D", border: "1px solid #C8CDD3" },
        description: `An error occurred. Please restart the Vision Core. Contact ${GROUNDLIGHT_SUPPORT_EMAIL} if the error persists`,
        Icon: (
          <Tooltip title="Partially Operational" arrow>
            <ErrorOutlineRounded style={{ color: "#EE3E54" }} />
          </Tooltip>
        ),
      };
    }

    if (allOnline) {
      return {
        styles: { backgroundColor: "#FFFFFF", color: "#49494D", border: "1px solid #C8CDD3", cursor: "pointer" },
        description: "",
        Icon: <ConnectedHubIcon />,
      };
    }

    return {
      styles: { backgroundColor: "#E5E9ED", color: "#49494D" },
      description: "Cannot connect to Vision Core. Check the Vision Core’s internet connection.",
      Icon: (
        <Tooltip title="Not Operational" arrow>
          <DisconnectedHubIcon />
        </Tooltip>
      ),
    };
  };

  return (
    <>
      <HubSubNavBar />
      <Box sx={{ height: "calc(100vh - 150px)", overflow: "auto" }}>
        {hubsLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress size={40} />
          </Box>
        )}
        {!hubsLoading && hubs?.length === 0 && (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="subtitle">
              No Vision Cores detected. Contact{" "}
              <Link href={`mailto:${GROUNDLIGHT_SUPPORT_EMAIL}`} sx={{ color: "primary.main" }}>
                {GROUNDLIGHT_SUPPORT_EMAIL}
              </Link>{" "}
              for help getting set up.
            </Typography>
          </Box>
        )}
        {!hubsLoading && hubs && hubs.length > 0 && (
          <Container maxWidth="md" sx={{ padding: "20px 30px!important" }}>
            <List sx={{ padding: 0 }}>
              {hubs.map((hub) => {
                const { styles, description, Icon } = getHubStylesByStatus(hub);
                return (
                  <ListItem
                    key={hub.id}
                    sx={[
                      {
                        display: "flex",
                        justifyContent: "space-between",
                        borderRadius: "10px",
                        padding: "13px 10px",
                        marginBottom: "20px",
                        overflow: "hidden",
                      },
                      styles,
                    ]}
                    onClick={() => {
                      const { main_app_online: mainAppOnline } = hub.status_detail || {};
                      if (hub.isRestarting) {
                        console.warn(`Cannot navigate to hub ${hub.id}: Hub is restarting.`);
                        return;
                      }

                      if (!mainAppOnline) {
                        console.warn(`Cannot navigate to hub ${hub.id}: Main app is not online.`);
                        return;
                      }

                      if (!hub.error) {
                        navigate(`/reef/hubs/${hub.id}`, { state: { selectedHub: hub } });
                      }
                    }}
                  >
                    <Box sx={{ display: "flex", gap: { xs: "16px", sm: "20px" }, alignItems: "center" }}>
                      {Icon}
                      <Tooltip title={hub.name} disableHoverListener={hub.name.length < 30} arrow>
                        <Typography variant="title" noWrap sx={{ maxWidth: { xs: "200px", sm: "250px" } }}>
                          {hub.name}
                        </Typography>
                      </Tooltip>
                      <Typography sx={{ display: { xs: "none", sm: "inline" } }}>
                        {description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: "20px" }}>
                      <HubActions hubId={hub.id} hubName={hub.name} disabled={hub.isLoading || hub.isRestarting} />
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Container>
        )}
      </Box>
    </>
  );
}
